<template>
    <SidePanelHeader></SidePanelHeader>
    <div class="loader-block-info" v-if="loading" role="alert" title="Data is loading">
        <div class="spinner" id="loading-bar-spinner">
            <div class="spinner-icon"></div>
        </div>
    </div>
    <div class="header-btn-block" v-on:click="onBack" v-if="!loading && !viewMoreView" role="navigation">
        <a href="javascript:void(0)" class="back-btn">
            <img src="@/assets/arrow-left.svg" alt="Back Button" />
            {{ $t("EntityInfoView.back") }}
        </a>
    </div>
    <div class="site-section" v-if="!loading && !viewMoreView" role="main">
        <DatasetFilterWarning 
            :initialSwitchValue="disableDatasetFilters"
            v-if="datasetsToHide.length > 0" 
            v-on:datasetFilterUpdate="(update: any) => { disableDatasetFilters = update }"
        ></DatasetFilterWarning>
        <div class="profile-section">
            <div v-for="(entity, index) in parsedEntityInfo" :key="index" class="profile-section-profile">
                <!--Entity Panel-->
                <EntityBanner
                    :firstEntity="index == 0"
                    :entity="entity"
                    :index="index"
                    :parsedEntityInfo="parsedEntityInfo"
                    :showDetailsIndex="showDetailsIndex"
                    :annotationsView="annotationsView"
                />

                <!--Entity Details Dropdown Panel-->
                <EntityDetailsDropdown
                    v-if="showDetailsIndex === index"
                    :data="entity"
                    :datasets="datasets"
                    :datasetsToDisplay="datasetsToDisplay"
                    :datasetsToHide="datasetsToHide"
                    :disableDatasetFilters="disableDatasetFilters"
                    v-on:update="
                        viewMoreView = true;
                        viewMoreEntity = entity;
                    "
                />
            </div>
        </div>

        <ViewMoreDetailsView
            v-if="
                parsedEntityInfo.length >= 3 ||
                (annotationsView && parsedEntityInfo.length == 2)
            "
            :data="parsedEntityInfo[parsedEntityInfo.length - 1]"
            :datasets="datasets"
            :datasetsToDisplay="datasetsToDisplay"
            :datasetsToHide="datasetsToHide"
            :isAnnotationView="annotationsView"
            :timelineState="timelineState"
            :disableDatasetFilters="disableDatasetFilters"
            hideBackButton
            isLastEntity
            v-on:datasetFilterUpdate="(update: any) => { disableDatasetFilters = update }"
        >
        </ViewMoreDetailsView>

        <ThirdLevelTimeline
            v-if="parsedEntityInfo.length >= 3 && parsedEntityInfo[parsedEntityInfo.length - 2].connectedEntities.length > 1"
            :eventList="
                parsedEntityInfo[parsedEntityInfo.length - 2].connectedEntities
            "
            :currentEvent="
                parsedEntityInfo[parsedEntityInfo.length - 1].mainEntityInfo
            "
            v-on:update="(update: any) => {
                            if (!update) {
                                parsedEntityInfo.pop();
                            } else {
                                timelinePartial = true;
                            }
                        }"
        >
        </ThirdLevelTimeline>

        <!--If there are no entities-->
        <NoResults full-length v-show="entitiesByCategory['All'].length == 0"></NoResults>

        <!--Category Selection-->
        <CategorySelection
            v-show="entitiesByCategory['All'].length > 0 && (displayCategorySelection || (annotationsView && aboutSubjectDisplay == null))"
            :entityCategories="annotationsView ? 
                annotationCategories :
                Object.keys(entitiesByCategory['Categories'])"
            :annotationsView="annotationsView"
            v-on:update="(aboutSubjectDisplaySelection: any) => { aboutSubjectDisplay = aboutSubjectDisplaySelection }"
        />

        <!--Connected Entities-->
        <div
            class="entity-detail-section"
            v-show="
                entitiesByCategory['All'].length > 0 &&
                (
                    (annotationsView && aboutSubjectDisplay != null && parsedEntityInfo.length != 2) ||
                    (!displayCategorySelection &&
                        !viewMoreView &&
                        parsedEntityInfo.length != 3 &&
                        !annotationsView)
                )
            "
        >
            <div class="categories-block">
                <div class="entity-title sub-entity-title entity-category-title">
                    <h3>{{ connectedEntityTitle }}</h3>
                    <EntityViewSelector
                        v-show="uriList.length === 2"
                        v-on:update="
                            (update: any) => {
                                timelineView =
                                    update.timelineView != null
                                        ? update.timelineView
                                        : timelineView;
                                listView =
                                    update.listView != null
                                        ? update.listView
                                        : listView;
                            }
                        "
                    />
                </div>

                <EntityList
                    v-show="listView"
                    v-on:change="loading = true"
                    :connectedEntityTitle="connectedEntityTitle"
                    :categoryToDisplay="categoryToDisplay"
                    :annotationsView="annotationsView"
                    :aboutSubjectDisplay="aboutSubjectDisplay ?? false"
                    :expandedEntityIndex="expandedEntityIndex"
                />

                <EntityTimeline
                    v-show="timelineView"
                    :categoryToDisplay="categoryToDisplay"
                    :expandedEntityIndex="expandedEntityIndex"
                />
            </div>
        </div>
    </div>

    <!--View More Details View-->
    <ViewMoreDetailsView
        v-if="viewMoreView"
        :data="viewMoreEntity"
        :datasets="datasets"
        :datasetsToDisplay="datasetsToDisplay"
        :datasetsToHide="datasetsToHide"
        :disableDatasetFilters="disableDatasetFilters"
        v-on:update="viewMoreView = !viewMoreEntity"
        v-on:datasetFilterUpdate="(update: any) => { disableDatasetFilters = update }"
    >
    </ViewMoreDetailsView>
</template>

<script lang="ts">
import { Vue, Options } from "vue-class-component";
import { getEntityTypeName } from "../helper/EntityTypeHelper";
import EntityResourceCard from "../components/EntityResourceCard.vue";
import ViewMoreDetailsView from "../components/ViewMoreDetailsView.vue";
import SidePanelHeader from "../components/SidePanelHeader.vue";
import ThirdLevelTimeline from "../components/ThirdLevelTimeline.vue";
import CategorySelection from "../components/CategorySelection.vue";
import EntityViewSelector from "../components/EntityViewSelector.vue";
import EntityList from "../components/EntityList.vue";
import EntityTimeline from "../components/EntityTimeline.vue";
import EntityBanner from "../components/EntityBanner.vue";
import EntityDetailsDropdown from "../components/EntityDetailsDropdown.vue";
import NoResults from "@/components/NoResults.vue";
import DatasetFilterWarning from "@/components/DatasetFilterWarning.vue";
import { fetchWithRetry } from "../helper/API";

interface PredicateRow {
    predicate: string;
    predicateLabel?: string;
    object: string;
    objectLabel?: string;
    type?: string;
    typeLabel?: string;
}


@Options({
    components: {
        EntityResourceCard,
        ViewMoreDetailsView,
        SidePanelHeader,
        ThirdLevelTimeline,
        CategorySelection,
        EntityViewSelector,
        EntityList,
        EntityTimeline,
        EntityBanner,
        EntityDetailsDropdown,
        NoResults,
        DatasetFilterWarning
    },
    watch: {
        $route() {
            const newUriList = JSON.parse(this.$route.params.uri as string);
            if (this.uriList.length > newUriList.length) {
                this.parsedEntityInfo.pop();
            }
            if (newUriList.length == 1) {
                this.displayEntitiesByCategory("Selection");
                this.showDetailsIndex = 0;
            } else if (newUriList.length == 2) {
                this.showDetailsIndex = 1;
            } else {
                this.showDetailsIndex = -1;
            }

            this.uriList = newUriList;
            this.viewMoreView = false;
            this.loading = true;
        },
        selectedCategory() {
            this.showCategoryEntities();
        },
    },
})
export default class EntityInfoView extends Vue {
    uriList: Array<string> = [];
    loading = true;
    showDetailsIndex = 0;
    expandedEntityIndex = -1;
    viewMoreView = false;
    viewMoreEntity: any = null;
    displayCategorySelection = true;
    selectedCategory: any = null;
    entitiesByCategory: any = { Categories: [], All: [] };
    parsedEntityInfo: any = [];
    timelineView = false;
    listView = true;
    annotationsView = false;
    aboutSubjectDisplay: any = null;
    timelinePartial = false;
    requestId = "";
    datasetsToDisplay: any = [];
    datasetsToHide: any = [];
    datasets: any = {};
    disableDatasetFilters = false;

    // This is the url for filling out the dataset labels
    apiURL = 
        "https://lincs-api.lincsproject.ca/api/dataset";

    // This is the authToken needed for the lincs-api's rate limiting
    authToken = 
        "Bearer eyJhbGciOiJSUzI1NiIsInR5cCIgOiAiSldUIiwia2lkIiA6ICJVcG5aOHZaTGNUemhTMWtzdGN2NDVkR205QXYwUmlTbElaQ0YzZEhxR1JRIn0.eyJleHAiOjE3NDIzMjMyMjcsImlhdCI6MTcxMDc4NzIyNywianRpIjoiMjU2OTU1YjUtOTkzNi00OGZmLThjMmMtYjdkOWRiMTliZjc0IiwiaXNzIjoiaHR0cHM6Ly9rZXljbG9hay5saW5jc3Byb2plY3QuY2EvcmVhbG1zL2xpbmNzIiwiYXVkIjpbInZlcnNkLXVpIiwicmVzZWFyY2hzcGFjZSIsImFkbWluLWNsaSIsImJyb2tlciIsImFjY291bnQiXSwic3ViIjoiYmQ5MTRkMTEtZmVkMi00ODllLWI0MGQtNDk0YjkzZDFmOGU4IiwidHlwIjoiQmVhcmVyIiwiYXpwIjoibGluY3MtYXBpIiwiYWNyIjoiMSIsImFsbG93ZWQtb3JpZ2lucyI6WyIvKiJdLCJyZWFsbV9hY2Nlc3MiOnsicm9sZXMiOlsiVXNlciIsImRlZmF1bHQtcm9sZXMtbGluY3MiLCJvZmZsaW5lX2FjY2VzcyIsInVtYV9hdXRob3JpemF0aW9uIl19LCJyZXNvdXJjZV9hY2Nlc3MiOnsidmVyc2QtdWkiOnsicm9sZXMiOlsidXNlciJdfSwicmVzZWFyY2hzcGFjZSI6eyJyb2xlcyI6WyJjb250cmlidXRvciIsImd1ZXN0Il19LCJhZG1pbi1jbGkiOnsicm9sZXMiOlsiYWRtaW4iXX0sImJyb2tlciI6eyJyb2xlcyI6WyJyZWFkLXRva2VuIl19LCJhY2NvdW50Ijp7InJvbGVzIjpbIm1hbmFnZS1hY2NvdW50IiwibWFuYWdlLWFjY291bnQtbGlua3MiLCJ2aWV3LXByb2ZpbGUiXX19LCJzY29wZSI6ImVtYWlsIHByb2ZpbGUgYXZhdGFyX3VybCIsImVtYWlsX3ZlcmlmaWVkIjpmYWxzZSwiY2xpZW50SG9zdCI6IjcwLjc1LjExMi4xMSIsInByZWZlcnJlZF91c2VybmFtZSI6InNlcnZpY2UtYWNjb3VudC1saW5jcy1hcGkiLCJjbGllbnRBZGRyZXNzIjoiNzAuNzUuMTEyLjExIiwiY2xpZW50X2lkIjoibGluY3MtYXBpIn0.ivjIKG0hWe5ULAseATO4RYUmR8GCEZaTnbE70zoT-xdNxz-jXZcGmTLnGf_t34X3BEX13u-unCPGv7T5M5Wnn6f_ohG5WW8H18oSxiAflSLue3TEpgEODACXS2rzP8gDODof2cC1MSDL9E_YI4lpQ6XAl8aMb-ojhzjZ4RJUbjXq3epQ-rmvg2R5DYtKSPtn8f1o2zooGVZg8lO_Ymi9tAxqr7PsayMXExsD2R_SShrUD9DS-1Vfvrh8a1dxdquUtZ1GHc90Ms08BxdV8hBe0Q8UvQorZgQ6dOUnKUId6250qcENqXjvbttN--FPt4z8BZDphaA38Itm-q0UV4YesQ";


    // Returns the displayed category
    get categoryToDisplay() {
        if (this.selectedCategory == "Selection") {
            return [];
        } else if (this.uriList.length == 1) {
            return this.entitiesByCategory["Categories"][this.selectedCategory];
        } else {
            return this.entitiesByCategory["All"];
        }
    }

    // Generates the title for the connected entities based on selected entity
    get connectedEntityTitle() {
        if (this.uriList.length == 1) {
            if (this.$i18n.locale == 'en') {
                return "Connected " + this.selectedCategory;
            } else {
                return this.selectedCategory + " connectées";
            }
        } else if (this.uriList.length == 2) {
            return this.$t("EntityInfoView.connecting_entities");
        } else {
            return this.$t("EntityInfoView.outbound_properties");
        }
    }

    get timelineState() {
        if (this.timelinePartial) {
            return 'partial';
        }

        return this.parsedEntityInfo[this.parsedEntityInfo.length - 2].connectedEntities.length > 1 ? 'full' : 'none';
    }

    get annotationCategories() {
        const output = [];
        const waCategory = this.$i18n.locale == 'en' ? 'Web References': 'Références Web';
        const aboutSubject = this.entitiesByCategory['Categories'][waCategory].filter((predicate: any) => {
            return (predicate as any).digitalObjects[0].isAboutSubject;
        });
        const mentionsSubject = this.entitiesByCategory['Categories'][waCategory].filter((predicate: any) => {
            return !(predicate as any).digitalObjects[0].isAboutSubject;
        });

        if (aboutSubject.length > 0) {
            output.push('Sources about ' + (this.parsedEntityInfo[0].mainEntityInfo.resourceLabel ? this.parsedEntityInfo[0].mainEntityInfo.resourceLabel : this.parsedEntityInfo[0].mainEntityInfo.resource));
        }
        if (mentionsSubject.length > 0) {
            output.push('Mentions of ' + (this.parsedEntityInfo[0].mainEntityInfo.resourceLabel ? this.parsedEntityInfo[0].mainEntityInfo.resourceLabel : this.parsedEntityInfo[0].mainEntityInfo.resource));
        }

        return output;
    }

    mounted() {
        this.$i18n.locale = this.$route.params.locale as string;
        this.uriList = JSON.parse(this.$route.params.uri as string);
        window.addEventListener("message", this.handleMessage);

        this.requestId = this.createRequestId(10);
        window.parent.postMessage({ type: "get-filters", id: this.requestId }, "*");

        this.datasets['https://www.wikidata.org'] = { name: "Wikidata", legendIcon: "WD" };
    }

    unmounted() {
        window.removeEventListener("message", this.handleMessage);
    }

    // MessageController for plugin
    handleMessage(event: any) {
        if (event.data.type == "update-entity-info") {
            this.parseOutgoingProperties(event);
            this.parsedEntityInfo.forEach((entityInfo: any, index: number) => {
                if (
                    entityInfo.mainEntityInfo.resource ==
                    event.data.response.mainEntityInfo.resource
                ) {
                    this.parsedEntityInfo[index] = event.data.response;
                }
            });
            this.sortByCategories();
        } else if (
            event.data.type == "send-entity-info" ||
            event.data.type == "send-related-entities"
        ) {
            if (
                this.parsedEntityInfo.length == 0 ||
                event.data.response.mainEntityInfo.resource !=
                    this.parsedEntityInfo[this.parsedEntityInfo.length - 1]
                        .mainEntityInfo.resource
            ) {
                this.parseOutgoingProperties(event);
                this.parsedEntityInfo.push(event.data.response);
            }
            this.sortByCategories();
            this.loading = false;
        } else if (event.data.type == "send-category") {
            this.viewMoreView = false;
            this.displayEntitiesByCategory(event.data.category);
        } else if (event.data.type == "send-annotation") {
            this.viewMoreView = false;
            this.displayWebReference(event.data.annotation);
        } else if (event.data.type == "return-filters" && event.data.id == this.requestId) {
            const filters = event.data.filters.activeFilters;
            this.datasetsToDisplay = filters.graph.filters
                .filter((filter: any) => filter.active)
                .map((filter: any) => filter.value);
            this.datasetsToHide = filters.graph.filters
                .filter((filter: any) => !filter.active)
                .map((filter: any) => filter.value);
        } else if (event.data.type == "set-loading") {
            this.loading = true;
        }
    }

    // Handle a back button press
    onBack() {
        if (this.parsedEntityInfo.length == 1) {
            if (this.annotationsView && this.aboutSubjectDisplay != null) {
                this.aboutSubjectDisplay = null;
                if (this.selectedCategory == "Web References" || this.selectedCategory == "Références Web") {
                    this.showCategoryEntities();
                } else {
                    this.selectedCategory = this.$i18n.locale == 'en' ? 'Web References': 'Références Web';
                }
            } else if (!this.displayCategorySelection) {
                this.displayCategorySelection = true;
                this.selectedCategory = 'Selection';
                this.annotationsView = false;
            } else {
                this.displayEntitySelect();
            }
        } else {
            this.listView = true;
            this.timelineView = false;
            this.toPreviousConnectedEntity();
        }
    }

    // Displays the entity select view
    displayEntitySelect() {
        window.parent.postMessage({ type: "show-entity-select" }, "*");
    }

    // To go back to the previous entity
    toPreviousConnectedEntity() {
        if (!this.annotationsView) {
            window.parent.postMessage(
                { type: "entity-traversal-back", amount: 1 },
                "*",
            );
        } else {
            this.parsedEntityInfo.pop();
            window.parent.postMessage({ type: "return-to-annotations" }, "*");
        }
    }

    // Toggles the dropdown open or closed
    handleToggleOfDetails(index: number, mainEntity: boolean) {
        if (mainEntity) {
            if (this.showDetailsIndex === index) {
                this.showDetailsIndex = -1;
            } else {
                this.showDetailsIndex = index;
            }
        } else {
            if (this.expandedEntityIndex === index) {
                this.expandedEntityIndex = -1;
            } else {
                this.expandedEntityIndex = index;
            }
        }
    }

    // To display the type of categories a selected entity has
    showCategoryEntities() {
        window.parent.postMessage(
            { type: "show-category-entities", category: this.selectedCategory },
            "*",
        );
    }

    // Displays the given category of entities
    displayEntitiesByCategory(category: string) {
        this.expandedEntityIndex = -1;
        if (category == "Sources about") {
            this.aboutSubjectDisplay = true;

            if (this.parsedEntityInfo.length > 1) {
                this.parsedEntityInfo.pop();
            }
        } else if (category == "Mentions of") {
            this.aboutSubjectDisplay = false;

            if (this.parsedEntityInfo.length > 1) {
                this.parsedEntityInfo.pop();
            }
        } else if (category != "Selection") {
            this.displayCategorySelection = false;
            this.annotationsView = (category == "Web References" || category == "Références Web");
            this.aboutSubjectDisplay = null;
            this.selectedCategory = category;
        } else {
            this.displayCategorySelection = true;
            this.annotationsView = false;
            this.selectedCategory = "Selection";
        }
    }

    // Displays a specific web reference
    displayWebReference(annotation: any) {
        const digitalObjectFormat = [];
        for (const digitalObject of annotation.digitalObjects) {
            digitalObjectFormat.push({
                predicateLabel: "Reference Location",
                object: digitalObject.uri,
                objectLabel:
                    digitalObject.label != "" ? digitalObject.label : null,
                objectIsURI: true,
                nonRsURI: true,
                image: "/cidoc-images/linguistic-object.svg",
                graph: annotation.graph
            });
        }

        const formattedAnnotation = {
            mainEntityInfo: {
                image: "/cidoc-images/linguistic-object.svg",
                resource: annotation.annotation != "" ? annotation.annotation : annotation.digitalObjects[0].uri,
                resourceLabel: annotation.digitalObjects[0].label,
                typeLabel:
                    "Annotation" +
                    (annotation.e55 != null ? "; " + annotation.e55 : ""),
                outgoing: new Array<any>(),
                graph: annotation.graph
            },
        };

        if (Object.prototype.hasOwnProperty.call(annotation, "quote")) {
            formattedAnnotation.mainEntityInfo.outgoing.push({
                predicateLabel: "Excerpt",
                object: annotation.quote,
                objectIsURI: false,
                graph: annotation.graph
            });
        }
        formattedAnnotation.mainEntityInfo.outgoing.push(
            ...digitalObjectFormat,
        );

        window.parent.postMessage(
            {
                type: "display-annotation-info",
                annotation: formattedAnnotation,
            },
            "*",
        );
        this.parsedEntityInfo.push(formattedAnnotation);
    }

    // Fetch all the types available for an entity and sort them into categories
    sortByCategories() {
        const categorizedPredicates: any = {};
        categorizedPredicates["All"] = [];
        categorizedPredicates["Categories"] = {};

        for (const connectedEntity of this.parsedEntityInfo[
            this.parsedEntityInfo.length - 1
        ].connectedEntities) {
            if (connectedEntity) {
                const url = connectedEntity.type ?? "Other";
                const entityType = url.split("/")[url.split("/").length - 1];
                const entityTypeName = getEntityTypeName(
                    entityType,
                    this.$i18n.locale != "fr",
                );

                if (!(entityTypeName in categorizedPredicates["Categories"]))
                    categorizedPredicates["Categories"][entityTypeName] = [];

                categorizedPredicates["Categories"][entityTypeName].push(
                    connectedEntity,
                );
                categorizedPredicates["All"].push(connectedEntity);
            }
        }

        this.entitiesByCategory = categorizedPredicates;
    }

    // Processes the outgoing props of the main entity
    parseOutgoingProperties(event: any) {
        if ('outgoing' in event.data.response.mainEntityInfo) {
            for (const property of event.data.response.mainEntityInfo.outgoing) {
                if (property.graph) {
                    this.processDataset(property.graph);
                }
            }
        }

        for (const property of event.data.response.connectedEntities) {
            if (property.graph) {
                this.processDataset(property.graph);
            }
        }
    }

    createRequestId(length: number) {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;
        let counter = 0;
        while (counter < length) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
            counter += 1;
        }
        return result;
    }

    processDataset(dataset: string) {
        if (!Object.keys(this.datasets).includes(dataset)) {
            this.datasets[dataset] = { };
            this.getDatasetLabel(dataset);
        }
    }

    getDatasetLabel(datasetUri: string) {
        const CRM_P1 = "http://www.cidoc-crm.org/cidoc-crm/P1_is_identified_by";
        const AAT_ABBREV = "http://vocab.getty.edu/aat/300417210"; // abbreviated titles
        const AAT_FULL   = "http://vocab.getty.edu/aat/300417209"; // full titles
        const AAT_ALT    = "http://vocab.getty.edu/aat/300417227"; // alternative titles

        fetchWithRetry(this.apiURL, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": this.authToken,
            },
            method: "POST",
            body: JSON.stringify({
                uri: datasetUri,
                language: this.$i18n.locale,
            }),
        })
        .then((response) => {
            if (response.ok) {
                return response.json() as Promise<PredicateRow[]>;
            }
            throw new Error("FAILURE");
        })
        .then((response: PredicateRow[]) => {

            const identifiers = response.filter(
                (pred) => pred.predicate === CRM_P1
            );

            if (!identifiers.length) {
                this.datasets[datasetUri].name = datasetUri;
                const lastSegment = datasetUri.split('/').pop() || datasetUri;
                this.datasets[datasetUri].legendIcon = lastSegment.slice(0, 2).toUpperCase();
                return;
            }

            // Prefer alternative then full, then anything. 
            const nameRow =
                identifiers.find((p) => p.type === AAT_ALT) ||
                identifiers.find((p) => p.type === AAT_FULL) ||
                identifiers[0];

            // Prefer abbreviated title for legend icon, fall back to name row
            const iconRow =
                identifiers.find((p) => p.type === AAT_ABBREV) ||
                nameRow;

            const name = nameRow.object;
            const iconText = iconRow.object;

            this.datasets[datasetUri].name = name;
            this.datasets[datasetUri].legendIcon = iconText;
        })
        .catch(() => {
            console.log("Query Error");
        });
}


}
</script>
<style>
.loader-block-info {
    position: fixed;
    top: calc(50vh - 35px);
    left: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    z-index: 999;
}

#loading-bar-spinner.spinner {
    animation: loading-bar-spinner 1000ms linear infinite;
}

#loading-bar-spinner.spinner .spinner-icon {
    width: 70px;
    height: 70px;
    border: solid 10px transparent;
    border-top-color: #083943 !important;
    border-left-color: #083943 !important;
    border-radius: 50%;
}

@keyframes loading-bar-spinner {
    0% {
        transform: rotate(0deg);
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
        transform: rotate(360deg);
    }
}

.icon-btn {
    display: flex;
    background-color: #107386;
    text-align: center;
    justify-content: center;
    align-items: center;
    border-radius: 10px;
    transition: 0.3s all ease;
    border: 2px solid #107386;
    transition: 0.2s all ease;
}

.icon-btn:hover {
    background-color: #4e98a6;
    border-color: #4e98a6;
    transition: 0.3s all ease;
}

.icon-btn img {
    width: 14px;
    background-color: transparent;
}

.icon-btn img.large-icon {
    width: 18px;
    padding: 4px;
}

.icon-btn.white-bg {
    background-color: white;
    padding: 0px;
    cursor: pointer;
}

.icon-btn.white-bg:hover {
    background-color: #c2dbe0;
}

.icon-btn.white-bg:hover img {
    background-color: transparent;
}

.large-icon {
    width: 20px;
}

.site-section {
    overflow-x: hidden;
    overflow-y: auto;
    right: 0px;
    top: 0;
    width: auto;
    height: calc(100vh - 98px);
    background-color: #ffffff;
    border-left: 1px solid #dddddd;
}

.profile-section {
    position: relative;
}

.profile-section.light-bg {
    background-color: #e7f1f3;
}

.profile-section.light-bg .pro-details-box p {
    color: #333333;
    width: 318px;;
}

.profile-section.light-bg .low-tone {
    background-color: #c2dbe0;
}

.profile-section.light-bg .pro-details-box h1 {
    color: #414343;
}

.profile-section-profile {
    margin-bottom: 3px;
    padding: 0px 8px;
}

.profile-details {
    flex: 1;
    text-align: left;
    position: relative;
    border-radius: 4px;
}

.profile-details.profile-details-data {
    height: 0px;
    visibility: hidden;
    opacity: 0;
    transition: 0.3s all ease;
}

.profile-details.profile-details-data.active {
    height: 201px;
    overflow: scroll;
    visibility: visible;
    opacity: 1;
    transition: 0.3s all ease;
}

.low-tone {
    background-color: #0a4652;
}

.pro-details-box {
    padding: 7px 8px 7px;
}

.pro-details-box p {
    margin: 0;
    color: white;
    font-size: 14px;
}


.entity-title {
    padding: 25px 20px 20px 15px;
    position: relative;
}

.sub-entity-title {
    padding: 25px 20px 20px 0px;
}

.entity-title h3 {
    font-size: 18px;
    margin: 0 0 0 0;
    font-weight: 500;
    color: #29313a;
}

.entity-title.entity-category-title {
    text-align: left;
    display: flex;
    justify-content: space-between;
    width: 100%;
}

.entity-detail-section {
    text-align: left;
    position: relative;
}

.entity-details-wrapper {
    position: relative;
    border: 1px solid #ccc;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 10px;
}

.categories-block {
    padding: 0px 12px 0px;
}

.header-btn-block {
    position: relative;
    margin: 0;
    display: flex;
    align-items: center;
    padding: 10px 15px 10px 15px;
}

.header-btn-block:hover {
    background-color: #edf6f8;
    transition: 0.2s ease all;
}

.back-btn {
    font-size: 16px;
    text-decoration: none;
    display: flex;
    align-items: center;
    color: #333333;
}

.back-btn img {
    margin-right: 10px;
}

.back-btn:hover {
    text-decoration: none;
}

.truncate-text {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.type-filter {
    font-family: 'Inter', sans-serif;
    color: #5E6364;
    width: calc(100% - 10px);
    height: 42px;
    border-radius: 10px;
    font-size: 14px;
    padding: 10px;
    margin-bottom: 10px;
    border-color: #cccccc;
}

.width-100 {
    width: 100% !important;
}
</style>
